import type React from "react";
import { useState, useEffect } from "react";
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Fab,
  Badge,
  Box,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import { Public, Favorite, Chat, Group, Add } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getConversationsREST } from "../../services/messageService";
import { getConversationsViaHub } from "../../services/signalRService";
import AuthModal from "../modals/AuthModal";
import WelcomeBackModal from "../modals/WelcomeBackModal";
import BrokerProfileRequiredModal from "../modals/BrokerProfileRequiredModal";
import PriceConversionModal from "../modals/PriceConversionModal";

export interface MobileBottomNavigationProps {
  selectedTab?: number;
}

export default function MobileBottomNavigation({
  selectedTab = 0,
}: MobileBottomNavigationProps) {
  const [bottomNavValue, setBottomNavValue] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  // Usar el hook de autenticación
  const {
    tokenKey,
    photoUrl,
    userToken,
    wasLoggedIn,
    isBroker,
    isAdmin,
    decodeIdToken,
    logout,
    setWasLoggedIn,
  } = useAuth();

  const { pageUrl } = useParams<{ pageUrl: string }>();

  // Estados para modales - manejados directamente
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [openWelcomeBackModal, setOpenWelcomeBackModal] = useState(false);
  const [openBrokerProfileRequiredModal, setOpenBrokerProfileRequiredModal] =
    useState(false);
  const [openConvertModal, setOpenConvertModal] = useState(false);

  // Estado para el menú desplegable
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Estado para el contador de mensajes
  const [messageCount, setMessageCount] = useState(0);

  // Convertir tokenKey a boolean para verificar si está logueado
  const isLoggedIn = !!tokenKey;

  // Funciones para manejar modales
  const handleAuthModalOpen = () => {
    setOpenWelcomeBackModal(false);
    setOpenAuthModal(true);
  };

  const handleAuthModalClose = () => {
    setOpenAuthModal(false);
  };

  const handleWelcomeBackModalOpen = () => {
    const googleToken = localStorage.getItem("googleIdToken");
    if (googleToken) {
      decodeIdToken(googleToken);
    }
    setOpenWelcomeBackModal(true);
  };

  const handleWelcomeBackModalClose = () => {
    setOpenWelcomeBackModal(false);
  };

  const handleBrokerProfileRequiredModalOpen = () => {
    setOpenBrokerProfileRequiredModal(true);
  };

  const handleBrokerProfileRequiredModalClose = () => {
    setOpenBrokerProfileRequiredModal(false);
  };

  const handleConvertModalOpen = () => {
    setOpenConvertModal(true);
    handleMenuClose();
  };

  const handleConvertModalClose = () => {
    setOpenConvertModal(false);
  };

  // Funciones para manejar el menú
  const handleMenuClick = (event: React.SyntheticEvent) => {
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    localStorage.removeItem("googleIdToken");
    setWasLoggedIn(false);
    logout();
    handleMenuClose();
  };

  // Función de navegación
  const handleNavigate = (path: string) => {
  if (location.pathname.startsWith("/company/") && pageUrl) {
    // compañía: concatenamos
    navigate(`/company/${pageUrl}/${path}`);
  } else {
    // fuera de compañía: ruta absoluta
    navigate(path.startsWith("/") ? path : `/${path}`);
  }
  handleMenuClose();
};


  // Función dinámica del botón principal
  const handleDynamicButtonClick = () => {
    if (tokenKey) {
      if ((isBroker || isAdmin) && pathname.startsWith("/properties")) {
        navigate("/properties/publish-property");
      } else if (pathname.startsWith("/posts")) {
        navigate("/posts/create-post");
      } else {
        handleBrokerProfileRequiredModalOpen();
      }
    } else {
      if (wasLoggedIn) {
        handleWelcomeBackModalOpen();
      } else {
        handleAuthModalOpen();
      }
    }
  };
  // Función para obtener el messageCount
  useEffect(() => {
    const fetchMessageCount = async () => {
      if (!tokenKey || !userToken) return;

      const getUserProfileId = () => {
        if (
          !userToken?.user?.profiles ||
          userToken.user.profiles.length === 0
        ) {
          return undefined;
        }

        const brokerProfile = userToken.user.profiles.find(
          (profile) => profile.roleName === "Broker"
        );
        if (brokerProfile) return brokerProfile.id;

        const clientProfile = userToken.user.profiles.find(
          (profile) => profile.roleName === "Cliente"
        );
        if (clientProfile) return clientProfile.id;

        return userToken.user.profiles[0].id;
      };

      try {
        const userProfileId = getUserProfileId();
        if (!userProfileId) return;

        let conversations: any[] = [];

        try {
          const signalRConversations = await getConversationsViaHub(
            userProfileId
          );
          if (Array.isArray(signalRConversations)) {
            conversations = signalRConversations;
          }
        } catch {
          const restConversations = await getConversationsREST(userProfileId);
          if (Array.isArray(restConversations)) {
            conversations = restConversations;
          }
        }

        const totalUnread = conversations.reduce(
          (acc, conv) => acc + (conv.unreadCount || 0),
          0
        );
        setMessageCount(totalUnread);
      } catch (error) {
        console.error("❌ Error al obtener el recuento de mensajes:", error);
      }
    };

    fetchMessageCount();
    const intervalId = setInterval(fetchMessageCount, 30000);
    return () => clearInterval(intervalId);
  }, [tokenKey, userToken]);

  // Actualizar el valor del BottomNavigation basado en la ruta actual
  useEffect(() => {
    if (location.pathname.includes("/properties/home")) {
      setBottomNavValue(0);
    } else if (location.pathname.includes("/properties/favorites")) {
      setBottomNavValue(1);
    } else if (location.pathname.includes("/properties/messages")) {
      setBottomNavValue(2);
    }
  }, [location.pathname]);

  const handleBottomNavChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setBottomNavValue(newValue);

    switch (newValue) {
      case 0:
        navigate("/properties/home");
        break;
      case 1:
        // Verificar autenticación para favoritos
        if (isLoggedIn) {
          navigate("/properties/favorites");
        } else {
          // Mostrar modal de autenticación
          if (wasLoggedIn) {
            handleWelcomeBackModalOpen();
          } else {
            handleAuthModalOpen();
          }
          // Resetear el valor a la pestaña anterior
          setBottomNavValue(0);
        }
        break;
      case 2:
        // Verificar autenticación para chat
        if (isLoggedIn) {
          navigate("/properties/messages");
        } else {
          // Mostrar modal de autenticación
          if (wasLoggedIn) {
            handleWelcomeBackModalOpen();
          } else {
            handleAuthModalOpen();
          }
          // Resetear el valor a la pestaña anterior
          setBottomNavValue(0);
        }
        break;
      case 3:
        // Abrir el menú cuando se presiona el botón de perfil
        handleMenuClick(event as React.MouseEvent<HTMLElement>);
        break;
    }
  };

  const realtorId = userToken?.user.profiles.find(p => p.roleName === "Broker")?.realtor?.id;

  return (
    <>
      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: "block", sm: "none" },
          zIndex: 1300,
          backgroundColor: "#000000",
        }}
        elevation={3}
      >
        {/*
        <Box display="flex" justifyContent="center" pt={1}>
          <InstallPWA />
        </Box> */}
        <BottomNavigation
          value={bottomNavValue}
          onChange={handleBottomNavChange}
          sx={{
            backgroundColor: "transparent",
            borderRadius: "20px 20px 0 0",
            height: "70px",
            paddingBottom: "10px",
            "& .MuiBottomNavigationAction-root": {
              color: "#ffffff",
              minWidth: "auto",
              "&.Mui-selected": {
                color: "#00913f",
              },
              "& .MuiBottomNavigationAction-label": {
                fontSize: "0.75rem",
                marginTop: "4px",
              },
            },
          }}
        >
          <BottomNavigationAction
            label="Inicio"
            icon={<Public sx={{ fontSize: "24px" }} />}
          />
          <BottomNavigationAction
            label="Favoritos"
            icon={
              isLoggedIn ? (
                <Badge badgeContent={0} color="error" invisible={true}>
                  <Favorite sx={{ fontSize: "24px" }} />
                </Badge>
              ) : (
                <Favorite sx={{ fontSize: "24px" }} />
              )
            }
          />
          <BottomNavigationAction
            label="Chat"
            icon={
              <Badge
                badgeContent={messageCount}
                color="success"
                invisible={messageCount === 0}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "#00913f",
                    color: "white",
                    fontSize: "0.7rem",
                    height: "18px",
                    minWidth: "18px",
                  },
                }}
              >
                <Chat sx={{ fontSize: "24px" }} />
              </Badge>
            }
          />
          <BottomNavigationAction
            label={isLoggedIn ? "Perfil" : "Acceder"}
            icon={
              isLoggedIn && photoUrl ? (
                <Box
                  component="img"
                  src={photoUrl}
                  alt="Profile"
                  sx={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    border: "2px solid #4CAF50",
                  }}
                />
              ) : (
                <Group sx={{ fontSize: "24px" }} />
              )
            }
          />
        </BottomNavigation>
        {/* Botón flotante para publicar (siempre visible) */}
        <Fab
          color="primary"
          aria-label="add"
          onClick={handleDynamicButtonClick}
          sx={{
            position: "absolute",
            top: -28,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#00913f",
            "&:hover": {
              backgroundColor: "#45a049",
            },
            zIndex: 1301,
          }}
        >
          <Add />
        </Fab>
      </Paper>

      {/* Menú desplegable - igual que en el Header */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            width: "200px",
          },
        }}
        sx={{
          zIndex: 1400, // Mayor que el navbar
        }}
      >
        {!tokenKey
          ? [
            <MenuItem
              key="login"
              onClick={() => {
                wasLoggedIn
                  ? handleWelcomeBackModalOpen()
                  : handleAuthModalOpen();
                handleMenuClose();
              }}
            >
              Iniciar Sesión
            </MenuItem>,
            <MenuItem
              key="register"
              onClick={() => {
                wasLoggedIn
                  ? handleWelcomeBackModalOpen()
                  : handleAuthModalOpen();
                handleMenuClose();
              }}
            >
              Registrarse
            </MenuItem>,
            <Divider key="divider2" />,
            <MenuItem key="convert" onClick={handleConvertModalOpen}>
              Convertir Precio
            </MenuItem>,
            <Divider key="divider3" />,
            <MenuItem
              key="download-android-guest"
              onClick={() => {
                window.location.href =
                  "https://play.google.com/store/apps/details?id=com.orbitas.alien_realty_app";
                handleMenuClose();
              }}
            >
              Descarga para Android
            </MenuItem>,
            <MenuItem
              key="download-ios-guest"
              onClick={() => {
                window.location.href = 'https://apps.apple.com/cr/app/alien-realty-app/id6738670454'
                handleMenuClose()
              }}
            >
              Descarga para iOS
            </MenuItem>,
          ]
          : [
            <MenuItem key="logout" onClick={handleLogout}>
              Cerrar sesión
            </MenuItem>,
            <Divider key="divider2" />,

            ...(isBroker
              ? [
                  <MenuItem
                    key="profile"
                    onClick={() => handleNavigate(`/properties/realtor/${realtorId}`)}
                  >
                    Ver perfil
                  </MenuItem>,
                ]
              : []),

            <MenuItem
              key="messages"
              onClick={() => handleNavigate("/properties/messages")}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <span>Ver mensajes</span>
                {messageCount > 0 && (
                  <Badge
                    badgeContent={messageCount}
                    invisible={messageCount === 0}
                    color="success"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.75rem",
                        height: "20px",
                        minWidth: "20px",
                        backgroundColor: "#4caf50",
                        color: "white",
                      },
                    }}
                  />
                )}
              </Box>
            </MenuItem>,

            ...(selectedTab === 0
              ? [
                  <MenuItem
                    key="favorites"
                    onClick={() => handleNavigate("/properties/favorites")}
                  >
                    Lista de favoritos
                  </MenuItem>,
                ]
              : []),

            <MenuItem key="modules" onClick={() => handleNavigate("/properties/modules-page")}>
              Módulos
            </MenuItem>,

            ...(tokenKey && (isBroker || isAdmin)
              ? [
                  <MenuItem
                    key="publish"
                    onClick={() =>
                      handleNavigate(
                        selectedTab === 0
                          ? "/properties/publish-property"
                          : "/posts/create-post"
                      )
                    }
                  >
                    {selectedTab === 0
                      ? "Publica una propiedad"
                      : "Publica una oportunidad"}
                  </MenuItem>,
                ]
              : []),

            <MenuItem key="convert" onClick={handleConvertModalOpen}>
              Convertir precio
            </MenuItem>,
            <MenuItem
              key="admin-account"
              onClick={() => {
                window.location.href =
                  "https://alienrealtyweb-admin-cbhmaugqcdbkcnf3.centralus-01.azurewebsites.net/admin-panel/home";
                handleMenuClose();
              }}
            >
              Administra tu cuenta
            </MenuItem>,
            <Divider key="divider3" />,
            <MenuItem
              key="download-android"
              onClick={() => {
                window.location.href =
                  "https://play.google.com/store/apps/details?id=com.orbitas.alien_realty_app";
                handleMenuClose();
              }}
            >
              Descarga para Android
            </MenuItem>,
            <MenuItem
              key="download-ios-guest"
              onClick={() => {
                window.location.href = 'https://apps.apple.com/cr/app/alien-realty-app/id6738670454'
                handleMenuClose()
              }}
            >
              Descarga para iOS
            </MenuItem>,
          ]}
      </Menu>

      {/* Modales - renderizados directamente */}
      {openAuthModal && (
        <AuthModal open={openAuthModal} onClose={handleAuthModalClose} />
      )}

      {openWelcomeBackModal && (
        <WelcomeBackModal
          open={openWelcomeBackModal}
          onClose={handleWelcomeBackModalClose}
          onSignIn={handleAuthModalOpen}
        />
      )}

      <BrokerProfileRequiredModal
        open={openBrokerProfileRequiredModal}
        onClose={handleBrokerProfileRequiredModalClose}
      />

      {openConvertModal && (
        <PriceConversionModal
          open={openConvertModal}
          onClose={handleConvertModalClose}
        />
      )}
    </>
  );
}
